import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useFriskOppHypotese, useHypoteseOvergang } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteseMedNummer, type Hypothesis, type UpdateFrequency } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { Sporingslinje } from '../innboks/Sporingslinje.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { HypotesePille } from '../status/Pille.tsx';
import { hypoteseOppforing } from '../status/status.ts';
import { Brikker, Varsel } from './deler.tsx';
import { Felt, sporFor } from './HDetalj.tsx';
import { Skjelettrader } from './HListe.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { baselineLaast, DAG_MS, hvem, isoDag, navnAv, overstyring, statusNavn, ventendeJustering } from './lesning.ts';
import { delsetning, kortDato, kortDatoAv, tall } from './setning.ts';
import { fraHypotese } from './skjema.ts';

/**
 * Oppstartskortet (H2.2 andre runde, del 3) på /h/{nr}/start — eierens side før «Start testen».
 * Formen er konseptet v0.4 W10 (DD-71): en egen flate med «‹ Til deg», crumb, tittel og pillen, og
 * to kolonner — sporingslinjen, setningen og feltene til venstre; kortet «Før start» til høyre med
 * sjekklista, målepunktene og «Start testen» og «Juster før start» INNE i kortet. Wireframen
 * (`hypotese-innsending.jsx`, Oppstart) eier tilstandene, reglene og ordlyden: Valgt er nå-leddet
 * (konseptet tegnet «Testes» som nå — wireframen gjelder), baseline er en LÅS med navnet på den som
 * kan løsne den, og i `startKlar` sier raden hvem som løsnet den og når. Ctrl+↵ er «Start testen».
 *
 * Portene er kontraktens (`HypothesisStartRequest`), i kontraktens rekkefølge, og klienten viser
 * den første som stenger FØR kallet — samme mønster som «Les over» i innsendingen. Tjeneren
 * kjører dem også; et 409 får norsk ordlyd (feil.ts), og hypotesen hentes på nytt så sjekklista
 * viser det som gjelder. Under to målepunkter er ikke en lås, men et valg: varselet med
 * «Start likevel» (`startAnyway: true`, lagres som overstyring `ett_maalepunkt`).
 *
 * Ingen angre på start: ordlyden låses (H14). Etter start går siden til detaljen i «Til deg» med
 * kvitteringen «Testen er startet.»
 */

const IKKE_MIN = 20;
const IKKE_MAKS = 500;

/** Intervallet bak to-målepunkter-regnestykket (H31; `UpdateFrequency`): `sjeldnere` gir alltid varselet. */
const INTERVALL: Partial<Record<UpdateFrequency, number>> = { daglig: 1, ukentlig: 7, maanedlig: 30, kvartalsvis: 91 };
const FREKVENS: Record<UpdateFrequency, string> = { daglig: 'daglig', ukentlig: 'ukentlig', maanedlig: 'månedlig', kvartalsvis: 'kvartalsvis', sjeldnere: 'sjeldnere enn kvartalsvis' };

/** Målepunktene i perioden: 1 + floor(testdager / intervall) — kontraktens `measurementPoints`, ren aritmetikk. */
export function maalepunkter(fra: string, til: string, frekvens: UpdateFrequency | undefined): number | null {
  if (!fra || !til || til <= fra) return null;
  const intervall = frekvens ? INTERVALL[frekvens] : undefined;
  if (!intervall) return 1;
  const dager = Math.round((Date.parse(`${til}T12:00:00Z`) - Date.parse(`${fra}T12:00:00Z`)) / DAG_MS);
  return 1 + Math.floor(dager / intervall);
}

/** «2.–30. nov», eller «28. okt–25. nov» når månedene er ulike. */
function periodeTekst(fra: string, til: string): string {
  return fra.slice(0, 7) === til.slice(0, 7) ? `${Number(fra.slice(8, 10))}.–${kortDato(til)}` : `${kortDato(fra)}–${kortDato(til)}`;
}

/** Hypothesis_incomplete ved start er feltene her, ikke formen (feil.ts har innsendingens setning). */
const START_TEKST: Record<string, string> = {
  hypothesis_incomplete: 'Fyll ut «Det vi ikke gjør» og testperioden før start.',
};

export function OppstartSide({ nr }: { nr: number }) {
  const smal = useSmal();
  const me = useMe();
  const hypotese = useHypoteseMedNummer(nr);
  const tilbake = `/h/til-deg/${nr}`;
  // Når skjemaet først står, blir det stående: en ny henting (etter et 409) skal ikke ta teksten.
  const vist = useRef<string | null>(null);

  const melding = (tittel: string, tekst: string) => (
    <div className="h-flate">
      {smal ? <Stripe tilbake={tilbake} tittel={tittel} /> : null}
      <div className="h-rull">
        <div className="h-innhold h-innhold-les">
          {smal ? null : <h1 className="panel-h1">{tittel}</h1>}
          <p className="hb-dempet">{tekst}</p>
          <div>
            <Link to={tilbake} className="panel-knapp-sekundaer">
              Til deg
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  if (hypotese.isPending || me.isPending) {
    return (
      <div className="h-flate">
        {smal ? <Stripe tilbake={tilbake} tittel={nummerTekst(nr)} /> : null}
        <Skjelettrader etikett="Henter hypotesen" />
      </div>
    );
  }
  const h = hypotese.data;
  // K2: en hypotese som ikke finnes, sier det samme som en du ikke har tilgang til.
  if (hypotese.isError || !h) {
    return melding(`Fant ikke ${nummerTekst(nr)}`, hypotese.isError && erNettfeil(hypotese.error) ? 'Fikk ikke hentet hypotesen. Sjekk nettet og prøv igjen.' : 'Den finnes ikke, eller den er ikke din å se. Sjekk nummeret.');
  }
  // Tjeneren vokter (403 not_owner); dette er speilet som forklarer først.
  if (h.owner?.id !== me.data?.id) {
    return melding('Oppstarten er eierens', `${navnAv(h.owner)} fyller ut oppstarten og starter testen; ${navnAv(h.decider)} avgjør.`);
  }
  // Startet, eller ikke valgt (lenger): detaljen viser det som gjelder.
  if (vist.current !== h.id && (h.status !== 'valgt' || h.startedAt)) return <Navigate to={tilbake} replace />;
  vist.current = h.id;
  return <Oppstart h={h} />;
}

function Stripe({ tilbake, tittel }: { tilbake: string; tittel: string }) {
  return (
    <header className="h-stripe" data-testid="ho-stripe">
      <Link to={tilbake} className="panel-stripe-tilbake" aria-label="Tilbake til Til deg">
        <Ikon navn="chevron-back-outline" />
      </Link>
      <h1 className="h-stripe-tittel">{tittel}</h1>
    </header>
  );
}

type Tilstand = 'ok' | 'laas' | 'aapen';
const SJEKKIKON: Record<Tilstand, string> = { ok: 'checkmark-outline', laas: 'lock-closed-outline', aapen: 'ellipse-outline' };

function Oppstart({ h }: { h: Hypothesis }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const megId = useMe().data?.id;
  const config = useHypoteseConfig();
  const overgang = useHypoteseOvergang();
  const friskOpp = useFriskOppHypotese();
  const nr = h.number!;
  const tilbake = `/h/til-deg/${nr}`;

  const [ikkeGjoer, setIkkeGjoer] = useState(h.notDoing ?? '');
  const [fra, setFra] = useState(h.testStartsOn ?? isoDag(new Date()));
  const [til, setTil] = useState(h.testEndsOn ?? '');
  const [ikkeRoert, setIkkeRoert] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  // Tjeneren regnet under to målepunkter (409) selv om klienten ikke gjorde det: varselet står.
  const [tjenerPunkter, setTjenerPunkter] = useState(false);

  const beslutter = navnAv(h.decider);
  const lengde = ikkeGjoer.trim().length;
  const punkter = maalepunkter(fra, til, h.updateFrequency);
  const venter = ventendeJustering(h);
  const kildeLoesnet = overstyring(h, 'uavklart_kilde');
  const baseLoesnet = overstyring(h, 'baseline_mangler');
  const kildeUavklart = h.sourceStatus === 'uavklart_definisjon' && !kildeLoesnet;
  const faaPunkter = (punkter !== null && punkter < 2) || tjenerPunkter;

  // Portene i kontraktens rekkefølge; den første som stenger, er grunnen ved knappen.
  const port: string | null = !h.ownerAcceptedAt
    ? 'Ta eierskapet først — det gjør du under «Til deg».'
    : lengde < IKKE_MIN
      ? `Skriv minst ${IKKE_MIN} tegn i «Det vi ikke gjør».`
      : lengde > IKKE_MAKS
        ? `Kort ned «Det vi ikke gjør» med ${lengde - IKKE_MAKS} tegn.`
        : !fra || !til
          ? 'Velg når testen starter og slutter.'
          : til <= fra
            ? 'Testen må slutte etter at den starter.'
            : venter
              ? `Justeringen venter på ${beslutter}. Testen starter på en godkjent versjon.`
              : h.sourceStatus === 'maa_bygges'
                ? 'Kilden må bygges før testen kan starte. Den låsen kan ingen løsne.'
                : baselineLaast(h)
                  ? `Baseline er ikke målt. ${beslutter} kan løsne låsen.`
                  : kildeUavklart
                    ? `Kilden er ikke verifisert. ${beslutter} kan løsne låsen.`
                    : null;
  const grunn = port ?? (faaPunkter ? 'Forleng perioden, eller start likevel.' : null);
  const opptatt = overgang.isPending;

  async function start(likevel: boolean) {
    if (opptatt || port || (faaPunkter && !likevel)) return;
    setFeil(null);
    try {
      await overgang.mutateAsync({ id: h.id, overgang: { handling: 'start', body: { notDoing: ikkeGjoer.trim(), testStartsOn: fra, testEndsOn: til, ...(likevel ? { startAnyway: true } : {}) } } });
      void naviger(tilbake, { state: { kvittering: 'Testen er startet.' } });
    } catch (e) {
      if (!erApiFeil(e)) return setFeil('Noe gikk galt. Det du skrev står. Prøv igjen.');
      if (erNettfeil(e)) return setFeil('Ingen nett. Det du skrev står. Prøv igjen når nettet er tilbake.');
      if (e.code === 'measurement_points_insufficient') setTjenerPunkter(true);
      // Noe har endret seg siden siden ble åpnet: sjekklista viser det som gjelder nå.
      else friskOpp(h.id, h.number);
      setFeil(START_TEKST[e.code] ?? e.brukertekst);
    }
  }

  // Ctrl+↵ er «Start testen» (wireframens tastatur), også med fokus i et felt.
  const startRef = useRef(start);
  startRef.current = start;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || e.defaultPrevented) return;
      e.preventDefault();
      void startRef.current(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // --- Sjekklista (W10): svaret på samme linje som kravet; en lås har ikon OG ord ----------
  const rader: Array<[string, string, Tilstand]> = [];
  rader.push(h.ownerAcceptedAt ? ['Eierskapet', `tatt ${kortDatoAv(h.ownerAcceptedAt)}`, 'ok'] : ['Eierskapet', 'ikke tatt ennå', 'laas']);
  rader.push(['Det vi ikke gjør', lengde === 0 ? 'ikke fylt' : lengde < IKKE_MIN ? 'for kort' : lengde > IKKE_MAKS ? 'for lang' : 'fylt', lengde >= IKKE_MIN && lengde <= IKKE_MAKS ? 'ok' : 'aapen']);
  rader.push(['Testperiode', !fra || !til ? 'ikke satt' : til <= fra ? 'slutter før den starter' : periodeTekst(fra, til), fra && til && til > fra ? 'ok' : 'aapen']);
  if (venter) rader.push(['Justering', `venter på ${hvem(h.decider, megId, 'deg')}`, 'laas']);
  if (h.sourceStatus === 'maa_bygges') {
    const bygger = h.measurementOwner ? ` · ${hvem(h.measurementOwner, megId, 'du')}${h.measurementReadyBy ? ` innen ${kortDato(h.measurementReadyBy)}` : ''}` : '';
    rader.push(['Kilden', `må bygges${bygger} · kan ikke løsnes`, 'laas']);
  } else if (h.sourceStatus === 'verifisert') {
    rader.push(['Kilden', `verifisert${h.sourceVerifiedBy ? ` av ${hvem(h.sourceVerifiedBy, megId, 'deg')}` : ''}`, 'ok']);
  } else if (kildeLoesnet) {
    rader.push(['Kilden', `løsnet av ${hvem(kildeLoesnet.by, megId, 'deg')} ${kortDatoAv(kildeLoesnet.at)}`, 'ok']);
  } else {
    rader.push(['Kilden', `ikke verifisert · ${beslutter} kan overstyre`, 'laas']);
  }
  if (baselineLaast(h)) rader.push(['Baseline', `ikke målt · ${beslutter} kan overstyre`, 'laas']);
  else if (baseLoesnet) rader.push(['Baseline', `løsnet av ${hvem(baseLoesnet.by, megId, 'deg')} ${kortDatoAv(baseLoesnet.at)}`, 'ok']);
  else rader.push(['Baseline', `${h.baselineValue != null ? `${tall(h.baselineValue)} ${h.unit ?? ''}`.trim() : 'målt'}${h.baselineOn ? ` · målt ${kortDato(h.baselineOn)}` : ''}`, 'ok']);

  const frekvens = h.updateFrequency ? `Tallet oppdateres ${FREKVENS[h.updateFrequency]}` : null;
  const punktTekst = punkter === null ? null : `${punkter} ${punkter === 1 ? 'målepunkt' : 'målepunkter'} i perioden`;
  const info = [frekvens, punktTekst].filter(Boolean).join(' · ');

  const valgtT = h.transitions.find((t) => t.toStatus === 'valgt' && t.fromStatus === 'vurderes');
  const hvemLinje = [valgtT ? `${kortDatoAv(valgtT.at)} av ${navnAv(valgtT.by)}` : null, h.ownerAcceptedAt ? `du tok eierskapet ${kortDatoAv(h.ownerAcceptedAt)}` : null].filter(Boolean).join(' · ');
  const ikkeUgyldig = ikkeRoert && lengde > 0 && (lengde < IKKE_MIN || lengde > IKKE_MAKS);

  const venstre = (
    <div className="ho-venstre">
      <Sporingslinje ledd={sporFor(h, config.data, megId, false)} etikett="Hvor hypotesen står" />
      <div data-testid="ho-setning">
        <Brikker deler={delsetning(fraHypotese(h))} />
      </div>
      <Felt id="ho-ikke" etikett="Det vi ikke gjør" teller={lengde >= IKKE_MAKS * 0.8 ? `${lengde} / ${IKKE_MAKS}` : undefined} grunn={ikkeUgyldig ? (lengde < IKKE_MIN ? `Minst ${IKKE_MIN} tegn.` : `Høyst ${IKKE_MAKS} tegn.`) : null}>
        <textarea id="ho-ikke" className="h-input h-tekstfelt" rows={2} value={ikkeGjoer} aria-invalid={ikkeUgyldig || undefined} onChange={(e) => setIkkeGjoer(e.target.value)} onBlur={() => setIkkeRoert(true)} />
      </Felt>
      <div className="ho-periode">
        <Felt id="ho-fra" etikett="Testen starter">
          <input
            id="ho-fra"
            type="date"
            className="h-input h-input-dato"
            value={fra}
            onChange={(e) => {
              setFra(e.target.value);
              setTjenerPunkter(false);
            }}
          />
        </Felt>
        <Felt id="ho-til" etikett="Testen slutter">
          <input
            id="ho-til"
            type="date"
            className="h-input h-input-dato"
            value={til}
            onChange={(e) => {
              setTil(e.target.value);
              setTjenerPunkter(false);
            }}
          />
        </Felt>
      </div>
      <p className="hb-dempet">Ordlyden låses når testen starter. Etterpå er en rettelse et funn.</p>
    </div>
  );

  const hoyre = (
    <section className="ho-kort" role="group" aria-labelledby="ho-kort-h">
      <h2 id="ho-kort-h" className="ho-kort-tittel">
        Før start
      </h2>
      <ul className="ho-sjekk">
        {rader.map(([navn, svar, t]) => (
          <li key={navn} className={`ho-sjekk-rad ho-sjekk-${t}`}>
            <Ikon navn={SJEKKIKON[t]} className="ho-st" />
            <span className="ho-k">{navn}</span>
            <span className="ho-v">
              {t === 'laas' ? <span className="panel-skjult">Låst: </span> : null}
              {svar}
            </span>
          </li>
        ))}
      </ul>
      {info ? (
        <div className="ho-info" data-testid="ho-info">
          <Ikon navn="information-circle-outline" className="ho-info-ikon" />
          <span>{info}</span>
        </div>
      ) : null}
      {faaPunkter && !port ? (
        // FORESLÅTT ORDLYD, IKKE SPESIFISERT: verken wireframen eller konsept v0.4 har teksten på
        // to-målepunkter-varselet (bare bildeteksten i v0.3 skjerm 8). Produkteier avgjør den.
        <div className="ho-info ho-punkter" data-testid="ho-punkter">
          <Ikon navn="warning-outline" className="ho-punkter-ikon" />
          <span className="ho-punkter-tekst">
            <span>Perioden gir under to målepunkter, så ingen kan se om tallet flytter seg. Forleng perioden, eller start likevel — det lagres med navnet ditt og datoen.</span>
            <button type="button" className="panel-knapp-sekundaer ho-likevel" aria-busy={opptatt || undefined} onClick={() => void start(true)}>
              Start likevel
            </button>
          </span>
        </div>
      ) : null}
      <div className="ho-knapper">
        <button
          type="button"
          className="panel-knapp ho-start"
          aria-disabled={Boolean(grunn) || opptatt || undefined}
          aria-busy={opptatt || undefined}
          aria-describedby={grunn ? 'ho-start-grunn' : undefined}
          aria-keyshortcuts="Control+Enter"
          onClick={() => void start(false)}
          data-testid="ho-start"
        >
          <Ikon navn={grunn ? 'lock-closed-outline' : hypoteseOppforing('testes').ikon} />
          {opptatt ? 'Starter …' : 'Start testen'}
          {smal || opptatt ? null : <kbd>Ctrl ↵</kbd>}
        </button>
        {grunn ? (
          <span id="ho-start-grunn" className="h-grunn ho-grunn" data-testid="ho-grunn">
            <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
            <span>{grunn}</span>
          </span>
        ) : null}
        {venter ? null : (
          <Link to={`/h/til-deg/${nr}/juster`} className="panel-knapp-sekundaer ho-juster">
            Juster før start
          </Link>
        )}
      </div>
    </section>
  );

  return (
    <section className="hb-detalj ho-side" aria-labelledby="ho-tittel" data-testid="ho-side">
      {smal ? <Stripe tilbake={tilbake} tittel={nummerTekst(nr)} /> : null}
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold ho-innhold">
          {smal ? null : (
            <Link to={tilbake} className="panel-knapp-lenke hb-tilbake">
              <Ikon navn="chevron-back-outline" />
              Til deg
            </Link>
          )}
          <div className="hb-spor-topp">
            <span data-testid="ho-crumb">{`${nummerTekst(nr)} · fra ${hvem(h.submittedBy, megId, 'deg')}${h.submittedAt ? ` ${kortDatoAv(h.submittedAt)}` : ''}`}</span>
            <span className="hb-roller" data-testid="ho-roller">{`Du eier · ${beslutter} avgjør`}</span>
          </div>
          <div className="ho-hode">
            <h1 id="ho-tittel" className="hb-tittel">
              {h.title}
            </h1>
            <div className="hb-naa-pille">
              <HypotesePille noekkel="valgt" navn={statusNavn(config.data, h) || 'Valgt'} />
              {hvemLinje ? (
                <span className="hb-dempet" data-testid="ho-hvem">
                  {hvemLinje}
                </span>
              ) : null}
            </div>
          </div>
          {feil ? <Varsel>{`Testen ble ikke startet. ${feil}`}</Varsel> : null}
          <div className="ho-to">
            {venstre}
            {hoyre}
          </div>
        </div>
      </div>
    </section>
  );
}
